/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

import MedaicaApiService from "./"
import {
  BookingAccount,
  BookingConfig,
  BookingRequest,
  BookingResult,
  BookingSession,
  BookingSlot,
  BookingVerifyResult,
  QuestionnaireDefinition,
} from "@medaica/common/types"

/**
 * D2C booking wizard API (05 §2, contract 03 §3). The anonymous gate calls
 * (createSession / verify / resendCode) go over `apiClient(false)`. The
 * session-scoped calls run over `apiClient()` and therefore require this
 * BookingApi to hang off a MedaicaApiService whose `accessTokenProvider`
 * returns the booking session token — i.e. a private, token-closure instance
 * (the exam-room precedent), NOT the global Auth0-wired service.
 *
 * Contract note: `verify` / `resendCode` keep the frozen `{ token, code }` /
 * `{ token }` wire shape; the `token` value is the `verificationToken` returned
 * by `createSession` (uniform across the ?t= and tokenless paths). The only
 * proposed 03 §3 addition is that `verificationToken` — see INTEGRATION-NOTES.
 */
class BookingApi {
  private readonly _apiService: MedaicaApiService

  constructor(apiService: MedaicaApiService) {
    this._apiService = apiService
  }

  // --- anonymous (pre-session) ---

  async createSession(params: { token?: string; email?: string }): Promise<BookingSession> {
    const apiClient = await this._apiService.apiClient(false)
    const response = await apiClient.post<BookingSession>("/booking/session", params)
    return response.data
  }

  async verify(verificationToken: string, code: string): Promise<BookingVerifyResult> {
    const apiClient = await this._apiService.apiClient(false)
    const response = await apiClient.post<BookingVerifyResult>("/booking/verify", {
      token: verificationToken,
      code,
    })
    return response.data
  }

  async resendCode(verificationToken: string): Promise<void> {
    const apiClient = await this._apiService.apiClient(false)
    await apiClient.post("/booking/resend-code", { token: verificationToken })
  }

  // --- session-scoped (Authorization: Bearer <booking session token>) ---

  async getAccount(): Promise<BookingAccount> {
    const apiClient = await this._apiService.apiClient()
    const response = await apiClient.get<BookingAccount>("/booking/account")
    const account = response.data
    // earliestExpiry is a DATE-ONLY field ("2027-07-31") — the interceptor's ISO
    // coercion is (correctly) datetime-only, so re-hydrate it here. Local-parts parse:
    // new Date("YYYY-MM-DD") is UTC midnight, which can shift the displayed month back
    // a day/month in negative-offset zones.
    if (typeof account.earliestExpiry === "string") {
      const [year, month, day] = (account.earliestExpiry as string).split("-").map(Number)
      account.earliestExpiry = new Date(year, month - 1, day)
    }
    return account
  }

  async getQuestionnaire(): Promise<QuestionnaireDefinition> {
    const apiClient = await this._apiService.apiClient()
    const response = await apiClient.get<QuestionnaireDefinition>("/booking/questionnaire")
    return response.data
  }
  async getBookingConfig(): Promise<BookingConfig> {
    const apiClient = await this._apiService.apiClient()
    const response = await apiClient.get<BookingConfig>("/booking/config")
    return response.data
  }

  async getAvailableSlots(from: Date, to: Date, timeZone: string): Promise<BookingSlot[]> {
    const apiClient = await this._apiService.apiClient()
    const response = await apiClient.get<BookingSlot[]>(
      this._apiService.withQuery("/booking/available-slots", {
        from: from.toISOString(),
        to: to.toISOString(),
        tz: timeZone,
      })
    )
    return response.data
  }

  async bookAppointment(request: BookingRequest): Promise<BookingResult> {
    const apiClient = await this._apiService.apiClient()
    const response = await apiClient.post<BookingResult>("/booking/appointments", request)
    return response.data
  }
}

export default BookingApi
